import { useState } from 'react';
import { useToggle } from '@hooks/useToggle';
import type { DelegationRole } from '@utils/user';
import { QuestionStatusStats, UserDelegationStats } from '../../../types/initiativeStats';
import { MessageFormField, MessageFormProps } from '../types';
import {
  DelegationSource,
  getDelegationSource,
  getFilteredContributors,
  getFilteredVerifiers,
  getSystemHint,
} from '../utils';
import { QuestionsFilter } from './QuestionsFilter';
import { getFilterOptions } from './QuestionsOptions';
import { ContributorVerifierMessageForm } from './ContributorVerifierMessageForm';
import { SystemUsersToggle } from './SystemUsersToggle';
import { NoExplicitUsersNote } from './NoExplicitUsersNote';

interface BulkSurveysMessageFormProps extends MessageFormProps {
  users: UserDelegationStats[];
}

export const BulkSurveysMessageForm = (props: BulkSurveysMessageFormProps) => {
  const { users, onChange } = props;
  const [selectedStatus, setSelectedStatus] = useState<QuestionStatusStats | undefined>(undefined);
  const [showSystemUsers, toggleShowSystemUsers] = useToggle(false);
  const source = getDelegationSource(showSystemUsers);

  const selectRecipients = (value: QuestionStatusStats, nextSource: DelegationSource) => {
    onChange(MessageFormField.ContributorIds)(getFilteredContributors(users, value, nextSource).map((user) => user._id));
    onChange(MessageFormField.VerifierIds)(getFilteredVerifiers(users, value, nextSource).map((user) => user._id));
  };

  const onChangeQuestionsFilter = (value: QuestionStatusStats) => {
    if (value === selectedStatus) {
      return;
    }
    setSelectedStatus(value);
    selectRecipients(value, source);
  };

  const onToggleSystemUsers = () => {
    toggleShowSystemUsers();
    if (selectedStatus) {
      selectRecipients(selectedStatus, getDelegationSource(!showSystemUsers));
    }
  };

  // Hints only make sense once system users are mixed in with explicit delegates
  const withHint = (list: UserDelegationStats[], role: DelegationRole) =>
    list.map((user) => ({ ...user, hint: showSystemUsers ? getSystemHint(user, role) : undefined }));

  const contributors = selectedStatus ? withHint(getFilteredContributors(users, selectedStatus, source), 'contributor') : [];
  const verifiers = selectedStatus ? withHint(getFilteredVerifiers(users, selectedStatus, source), 'verifier') : [];
  // Explain the empty list when system delegated users would match with the toggle on
  const onlySystemUsersMatch =
    !showSystemUsers &&
    selectedStatus !== undefined &&
    contributors.length === 0 &&
    verifiers.length === 0 &&
    (getFilteredContributors(users, selectedStatus, 'totalDelegation').length > 0 ||
      getFilteredVerifiers(users, selectedStatus, 'totalDelegation').length > 0);

  return (
    <>
      <div className='row'>
        <div className='col-7'>
          <QuestionsFilter label='Message users with' options={getFilterOptions} onChange={onChangeQuestionsFilter} />
        </div>
        <div className='col-5 d-flex align-items-end justify-content-end pb-1'>
          <SystemUsersToggle checked={showSystemUsers} onChange={onToggleSystemUsers} />
        </div>
      </div>
      {onlySystemUsersMatch ? <NoExplicitUsersNote className='mb-2' /> : null}
      <ContributorVerifierMessageForm {...props} contributors={contributors} verifiers={verifiers} />
    </>
  );
};
